'use client';

import { markOAuthRedirectStart, type IFirebaseOAuthRedirectTracking } from '@/lib/auth/firebase-oauth-session';

import { getFacebookAppId } from './config';

const SESSION_KEY_FACEBOOK_OAUTH_STATE = 'facebook_oauth_state';
const FB_OAUTH_DIALOG = 'https://www.facebook.com/v21.0/dialog/oauth';

/** 与静态导出的 login.html 一致，避免 /oauth/... 深层路径未部署导致 404 */
export function getFacebookOAuthRedirectUri(): string {
    if (typeof window === 'undefined') {
        return '';
    }
    return `${window.location.origin}/login`;
}

/**
 * 移动端浏览器整页跳转 Facebook OAuth（implicit flow，token 在 /login#... 回传）。
 * 须在 Meta 应用「Valid OAuth Redirect URIs」中配置 getFacebookOAuthRedirectUri()。
 */
export function startFacebookOAuthRedirect(returnTo: string, tracking: IFirebaseOAuthRedirectTracking): void {
    const appId = getFacebookAppId();
    const redirectUri = getFacebookOAuthRedirectUri();
    const state = crypto.randomUUID();

    try {
        sessionStorage.setItem(SESSION_KEY_FACEBOOK_OAUTH_STATE, state);
    } catch {
        // ignore
    }

    markOAuthRedirectStart(returnTo, tracking, { skipLoginAttempt: true });

    const params = new URLSearchParams({
        client_id: appId,
        redirect_uri: redirectUri,
        response_type: 'token',
        scope: 'public_profile,email',
        state,
    });

    window.location.assign(`${FB_OAUTH_DIALOG}?${params.toString()}`);
}

export type TFacebookOAuthHashResult =
    | { accessToken: string }
    | { error: 'access_denied' | 'invalid_state' | 'oauth_error' };

export function parseFacebookOAuthHash(): TFacebookOAuthHashResult | null {
    if (typeof window === 'undefined') {
        return null;
    }
    const rawHash = window.location.hash;
    if (!rawHash || rawHash.length <= 1) {
        return null;
    }

    const params = new URLSearchParams(rawHash.startsWith('#') ? rawHash.slice(1) : rawHash);
    const error = params.get('error');
    if (error === 'access_denied') {
        return { error: 'access_denied' };
    }
    if (error) {
        return { error: 'oauth_error' };
    }

    const accessToken = params.get('access_token');
    if (!accessToken) {
        return null;
    }

    const state = params.get('state');
    let expectedState: string | null = null;
    try {
        expectedState = sessionStorage.getItem(SESSION_KEY_FACEBOOK_OAUTH_STATE);
        sessionStorage.removeItem(SESSION_KEY_FACEBOOK_OAUTH_STATE);
    } catch {
        // ignore
    }

    if (!state || !expectedState || state !== expectedState) {
        return { error: 'invalid_state' };
    }

    return { accessToken };
}

export function clearFacebookOAuthHashFromUrl(): void {
    if (typeof window === 'undefined') {
        return;
    }
    const path = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, '', path);
}
